import React, { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
import { Link } from 'gatsby';

import Section from '@components/Section';
import mediaqueries from '@styles/media';
import { IAuthor } from '@types';
import Headings from '@components/Headings';
import Image, { ImagePlaceholder } from '@components/Image';

import { GridLayoutContext } from './Articles.List.Context';

const authorQuery = graphql`
  {
    allArticle(limit: 4, sort: {order: DESC, fields: date}) {
      totalCount
      edges {
        node {
          slug
          backgroundColor
          title
          date(formatString: "DD MMM YYYY")
          secret
          hero {
            childImageSharp {
              fluid(maxWidth: 653, quality: 95, base64Width: 1) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
  }
`;

const ArticlesHero: React.FC<IAuthor> = ({ authors }) => {
  const { gridLayout = 'tiles', hasSetGridLayout, setGridLayout } = useContext(
    GridLayoutContext,
  );

  const results = useStaticQuery(authorQuery);
  const articlesThatArentSecret = results.allArticle.edges.filter(edge => !edge.node.secret).reverse();
  const latestArticles = articlesThatArentSecret[articlesThatArentSecret.length - 1];

  return (
    <Container backgroundColor={latestArticles.node.backgroundColor}>
      <Section narrow id="Articles__Hero">
        <Wrapper>
          <HeadingContainer>
            <HeroHeading>Untold <strong>Stories</strong></HeroHeading>
            <InfoText>
              I write to practice, to find ideas, and to have somethings to tell. &nbsp; 
              <Anchor to="/archive" data-a11y="false">
                Archives
              </Anchor>
            </InfoText>
            <LatestArticleWrapper>
              <Eyebrow>
                Latest story—{latestArticles.node.date}
              </Eyebrow>
              <Anchor to={latestArticles.node.slug} data-a11y="false">
                {latestArticles.node.title}
              </Anchor>
            </LatestArticleWrapper>
          </HeadingContainer>
          <ImagesList to={latestArticles.node.slug} data-a11y="false">
            {articlesThatArentSecret.map((item, index) => {

              const min = Math.ceil(2);
              const max = Math.floor(30);
              const randomNumber = Math.floor(Math.random() * (max - min) + min);
              
              return (
                <ImageWrapper
                  key={index}
                  i={index}
                  style={{ 
                    transform: `rotate(${randomNumber}deg)`
                  }}
                >
                  <ImageWrapperInner>
                    <ImageContainer>
                        { item.node.hero && <Image src={item.node.hero.childImageSharp.fluid} />}
                    </ImageContainer>
                  </ImageWrapperInner>
                </ImageWrapper>
              )})}
          </ImagesList>
        </Wrapper>
      </Section>
    </Container>
  );
};

export default ArticlesHero;

const Container = styled.div<{
  backgroundColor: string;
}>`
  display: grid;
  position: relative;
  align-items: center;
  min-height: calc(100vh + 80px);
  background: ${p => p.backgroundColor ? p.theme.colors[p.backgroundColor]: p.theme.colors.decorColor01};
`;

const Wrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 64px;
  align-items: center;

  ${mediaqueries.desktop`
    grid-template-columns: 1fr;
    row-gap: 16px;
    padding-top: 160px;
    padding-bottom: 48px;
  `}

`;

const HeadingContainer = styled.div`
  position: relative;
  margin-top: 40px;
  
  ${mediaqueries.desktop`
    // margin-top: -220px;
  `}
  
`;
  
const HeroHeading = styled(Headings.h1)`
  color: ${p => p.theme.colors.primary};
  padding-bottom: 16px;

  a {
    color: ${p => p.theme.colors.accent};
  }

  ${mediaqueries.desktop`
    font-size: 56px
  `}
`;

const InfoText = styled.p`
  font-size: 20px;
  margin-top: 16px;
  line-height: 1.5;
  font-family: ${p => p.theme.fonts.body};
  color: ${p => p.theme.colors.secondary};
`;

const Anchor = styled(Link)`
  color: inherit;
  border-bottom: 2px solid ${p => p.theme.colors.secondary};

  &:hover,
  &:focus {
    color: ${p => p.theme.colors.accent};
    border-bottom-color: ${p => p.theme.colors.accent};
  }
`;

const ImagesList = styled(Link)`
  position: relative;
  height: 100%;
  transform: scale(.85);
  align-self: stretch;
  margin-top: -48px;

  ${mediaqueries.desktop`
    padding-top: 40px;
    transform: scale(.8);
    margin-top: 0;
    height: 50vh;
  `}
  
`;

const ImageWrapperInner = styled.div`
  position: relative;
`;

const ImageWrapper = styled.div<{ i: number }>`
  position: absolute;
  left: 0;
  right: 0;
  transition: transform 1.5s var(--ease-out-quart);
`;

const ImageContainer = styled.div`
  position: relative;
  transition: transform 0.2s var(--ease-out-quad);
  padding-top: 100%;
  height: none;
  overflow: hidden;
  box-shadow: rgb(0 0 0 / 25%) 2px 5px 27px;
  background-color: ${p => p.theme.colors.card};

  & > div {
    top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		position: absolute !important;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: block;
    opacity: 0;
  }
`;

const Eyebrow = styled.div`
  font-size: 14px;
  font-weight: ${p => p.theme.fontsWeight.medium};
  letter-spacing: 0;
  text-transform: uppercase;
  margin-top: 32px;
  margin-bottom: 8px;
`;

const LatestArticleWrapper = styled(Headings.h3)`

`;